import {
    Button,
    Dialog,
    DialogBody,
    DialogFooter,
    H2
} from "@blueprintjs/core";
import classNames from "classnames";
import { useId } from "react";
import css from "./NiraDialog.module.scss";

type NiraDialogGap = "none" | "small" | "large";

interface NiraDialogProps {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly title: React.ReactNode;
    /** Optional uppercase blue label rendered above the title. */
    readonly eyebrow?: React.ReactNode;
    /** Body content. */
    readonly children: React.ReactNode;
    /** Right-aligned footer actions (e.g. primary/confirm buttons). */
    readonly footerActions?: React.ReactNode;
    /** Left-aligned secondary footer content (e.g. an Archive button). */
    readonly footerLeft?: React.ReactNode;
    /** Applied to the Dialog for size/layout overrides. */
    readonly className?: string;
    /** Applied to the DialogBody for layout overrides (e.g. min-height: 0). */
    readonly bodyClassName?: string;
    /** Vertical gap between body children. */
    readonly gap?: NiraDialogGap;
    /** Disables the close button, Escape and outside clicks, e.g. while saving. */
    readonly isCloseDisabled?: boolean;
}

/**
 * Shared dialog shell: a white rounded panel with a blue-accented header
 * (optional eyebrow + title), a flex-column body, and a light-gray footer.
 *
 * The header is rendered explicitly (rather than via Blueprint's `title` prop)
 * so its elements can be styled through component-owned classes instead of
 * descendant selectors reaching into Blueprint's internal `.bp5-dialog-header`.
 */
export const NiraDialog: React.FC<NiraDialogProps> = ({
    isOpen,
    onClose,
    title,
    eyebrow,
    children,
    footerActions,
    footerLeft,
    className,
    bodyClassName,
    gap = "none",
    isCloseDisabled = false
}) => {
    const hasFooter = footerActions !== undefined || footerLeft !== undefined;
    const titleId = useId();

    return (
        <Dialog
            className={classNames(css["NiraDialog"], className)}
            isOpen={isOpen}
            onClose={onClose}
            canEscapeKeyClose={!isCloseDisabled}
            canOutsideClickClose={!isCloseDisabled}
            aria-labelledby={titleId}
        >
            <div className={css["NiraDialog-header"]}>
                <div className={css["NiraDialog-titlebox"]}>
                    {eyebrow !== undefined && (
                        <span className={css["NiraDialog-eyebrow"]}>
                            {eyebrow}
                        </span>
                    )}
                    <H2
                        id={titleId}
                        className={css["NiraDialog-title-name"]}
                        title={typeof title === "string" ? title : undefined}
                    >
                        {title}
                    </H2>
                </div>
                <Button
                    aria-label="Close"
                    className={css["NiraDialog-close"]}
                    icon="small-cross"
                    variant="minimal"
                    disabled={isCloseDisabled}
                    onClick={onClose}
                />
            </div>
            <DialogBody
                className={classNames(
                    css["NiraDialog-body"],
                    css[`NiraDialog-body--gap-${gap}`],
                    bodyClassName
                )}
            >
                {children}
            </DialogBody>
            {hasFooter && (
                <DialogFooter
                    className={css["NiraDialog-footer"]}
                    actions={footerActions}
                >
                    {footerLeft}
                </DialogFooter>
            )}
        </Dialog>
    );
};
